import type { ReactNode } from 'react';

import { cn } from '@heidi/utils';

/**
 * Raycast-style shimmering text — a soft highlight sweeps left-to-right
 * across the glyphs to signal "Heidi is thinking". Implemented with a
 * moving CSS mask (same technique as the Evidence ``InlineSearchBlock``
 * "Searching…" label) over the global ``ptb-text-shimmer`` keyframe, so
 * the element keeps its own colour and no new keyframe is needed.
 */
export function OverlayShimmerText({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={cn('inline-block', className)} style={OVERLAY_SHIMMER_STYLE}>
      {children}
    </span>
  );
}

/** Block-level shimmer wrapper for streaming markdown bodies. */
export function OverlayShimmerBlock({
  children,
  className,
  contentOnly = false,
}: {
  children: ReactNode;
  className?: string;
  /** Apply the mask only to editor content, leaving sibling toolbars untouched. */
  contentOnly?: boolean;
}) {
  return (
    <div
      className={cn(
        'min-w-0',
        contentOnly && OVERLAY_SHIMMER_CONTENT_ONLY_CLASS,
        className
      )}
      style={contentOnly ? undefined : OVERLAY_SHIMMER_STYLE}
    >
      {children}
    </div>
  );
}

/**
 * Content-only shimmer as Tailwind arbitrary descendant variants (same pattern
 * as `case-ask-heidi-input.tsx`'s EDITOR_CLASS), so it lives beside the shimmer
 * primitives instead of growing `globals.css`. Dims editor content (ProseMirror,
 * Draft.js, textarea) and sweeps the shared mask across it; toolbars and other
 * siblings stay untouched. `motion-safe:` scopes the sweep so reduced-motion
 * users keep the dim without the animation.
 *
 * The gradient duplicates `OVERLAY_SHIMMER_MASK_STYLE` below in Tailwind's
 * underscore syntax — it cannot be derived from the const because Tailwind's
 * JIT scanner only extracts statically written class strings. Keep in sync.
 */
export const OVERLAY_SHIMMER_CONTENT_ONLY_CLASS = [
  '[&_:is(.ProseMirror,.DraftEditor-root,textarea)]:text-text-secondary',
  '[&_:is(.ProseMirror,.DraftEditor-root,textarea)]:transition-colors',
  '[&_:is(.ProseMirror,.DraftEditor-root,textarea)]:duration-300',
  'motion-safe:[&_:is(.ProseMirror,.DraftEditor-root,textarea)]:[mask-image:linear-gradient(90deg,#000_0%,#000_35%,rgba(0,0,0,0.35)_50%,#000_65%,#000_100%)]',
  'motion-safe:[&_:is(.ProseMirror,.DraftEditor-root,textarea)]:[mask-size:200%_100%]',
  'motion-safe:[&_:is(.ProseMirror,.DraftEditor-root,textarea)]:[animation:ptb-text-shimmer_2s_ease-in-out_infinite]',
].join(' ');

/** Dims live streamed note/document body copy via `text-secondary` until the stream settles. */
export const STREAMING_CONTENT_MUTED_CLASS = [
  '[&_:is(.ProseMirror,.DraftEditor-root,textarea)]:!text-text-secondary',
  '[&_:is(.ProseMirror,.DraftEditor-root,textarea)]:transition-colors',
  '[&_:is(.ProseMirror,.DraftEditor-root,textarea)]:duration-300',
].join(' ');

const OVERLAY_SHIMMER_MASK_STYLE = {
  maskImage:
    'linear-gradient(90deg, #000 0%, #000 35%, rgba(0,0,0,0.35) 50%, #000 65%, #000 100%)',
  maskSize: '200% 100%',
  WebkitMaskImage:
    'linear-gradient(90deg, #000 0%, #000 35%, rgba(0,0,0,0.35) 50%, #000 65%, #000 100%)',
  WebkitMaskSize: '200% 100%',
} as const;

const OVERLAY_SHIMMER_STYLE = {
  ...OVERLAY_SHIMMER_MASK_STYLE,
  animation: 'ptb-text-shimmer 2s ease-in-out infinite',
} as const;

/**
 * Skeleton placeholder lines with a sweeping shimmer — shown in the
 * reply body before the first token lands so the wait reads as "answer
 * incoming" (Raycast's list/answer loading) rather than a blank gap.
 */
export function OverlayReplySkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-2 py-1', className)} aria-hidden="true">
      {['92%', '80%', '86%', '64%'].map((width, index) => (
        <div
          key={width + index}
          className="relative h-3 overflow-hidden rounded bg-surface-hover"
          style={{ width }}
        >
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/55 to-transparent" />
        </div>
      ))}
    </div>
  );
}
